import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * DChip — the desk's in-cell action chip (deskkit.dchip).
 *
 * This is where the one deliberate density divergence between phone and desk
 * lives: the phone is 44px everywhere, the desk is pointer-first with a 32px
 * visual inside a 44px hit box. `.dchip::before` in kit.css paints that
 * invisible 44px target, so the control looks tight in a table row while still
 * being comfortably clickable.
 *
 * OPEN QUESTION — `2 PLAN — DESKTOP.md` §11.5-7 asks whether any clinic desks
 * are touchscreens. If the answer is yes, every in-cell control grows and this
 * sizing changes. Do not harden it until Dr. Mostafa answers.
 *
 * Unlike Chip, a DChip is ALWAYS a control — it has no label-only mode, because
 * a status label on the desk is a StateChip.
 */

export type DChipKind = 'outline' | 'brand' | 'soft' | 'warn' | 'success' | 'danger';

const KIND_CLASS: Record<DChipKind, string> = {
  outline: 'dchip-outline',
  brand: 'dchip-brand',
  soft: 'dchip-soft',
  warn: 'dchip-warn',
  success: 'dchip-success',
  danger: 'dchip-danger',
};

interface DChipProps {
  children: ReactNode;
  /** Required. A dchip with nothing to do is a StateChip, not a dchip. */
  onClick: () => void;
  kind?: DChipKind;
  disabled?: boolean;
  /** Accessible name when the label alone is not descriptive out of context. */
  label?: string;
  className?: string;
}

export function DChip({
  children,
  onClick,
  kind = 'outline',
  disabled = false,
  label,
  className,
}: DChipProps) {
  return (
    <button
      type="button"
      className={clsx('dchip', KIND_CLASS[kind], className)}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      {children}
    </button>
  );
}
